// Fortune-500-style keepsake certificate for CoaterZ · Launch Partner
// Renders a portrait 8.5×11 SVG (viewBox 850×1100) in deep emerald + antique
// gold. Includes the EVIQ logo (embedded via <image>) and a QR code linking
// to https://eviq.pro/app.
//
// The QR module matrix is generated by the caller with the `qrcode` package
// and passed in as `qrModules`, so this file stays a pure string builder
// (matches the SVG-string architecture used by carSticker/reelSvg).

const escape = (s) =>
    String(s ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");

const EVIQ_LOGO_URL =
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-legacy/artifacts/fzv91rpw_EVIQ%20Logo.png";

const IMPACT_BADGE_URL =
    "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-legacy/artifacts/5bs7mro4_EVIQImpactPartner.png";

const GREEN = "#a3e635";
const GREEN_LIGHT = "#c8f37f";
const GREEN_DEEP = "#5a7f1a";
const CYAN = "#06b6d4";
const CYAN_LIGHT = "#67e8f9";
const BLACK = "#000000";
const BLACK_SOFT = "#0a0a0a";
const CREAM = "#f4f4f5";

// -- QR renderer -----------------------------------------------------------
// qrModules: { size: number, data: Uint8Array | boolean[] } from
// QRCode.create(text).modules. Rendered as a grid of lime rects on black.
const buildQrGroup = (qrModules, { x, y, size }) => {
    if (!qrModules) return "";
    const n = qrModules.size;
    const step = size / n;
    const data = qrModules.data;
    let rects = "";
    for (let row = 0; row < n; row++) {
        for (let col = 0; col < n; col++) {
            const on = data[row * n + col];
            if (!on) continue;
            const rx = x + col * step;
            const ry = y + row * step;
            rects += `<rect x="${rx.toFixed(2)}" y="${ry.toFixed(2)}" width="${(step + 0.3).toFixed(2)}" height="${(step + 0.3).toFixed(2)}" fill="${GREEN_LIGHT}"/>`;
        }
    }
    return `<g>${rects}</g>`;
};

// -- Corner flourishes -----------------------------------------------------
const cornerFlourish = (cx, cy, rot = 0) => `
  <g transform="translate(${cx} ${cy}) rotate(${rot})">
    <path d="M 0 0 L 46 0 M 0 0 L 0 46" stroke="${GREEN}" stroke-width="1.4" stroke-linecap="round"/>
    <path d="M 6 0 Q 20 6 26 20 Q 30 30 26 40" fill="none" stroke="${CYAN}" stroke-width="1"/>
    <circle cx="0" cy="0" r="3" fill="${GREEN_LIGHT}"/>
    <circle cx="0" cy="0" r="1.2" fill="${BLACK}"/>
  </g>
`;

// Filigree divider under the title
const filigree = (cx, cy, w = 260) => {
    const half = w / 2;
    return `
    <g transform="translate(${cx} ${cy})">
      <line x1="${-half}" y1="0" x2="${-24}" y2="0" stroke="url(#cz-accent)" stroke-width="0.9"/>
      <line x1="${24}" y1="0" x2="${half}" y2="0" stroke="url(#cz-accent)" stroke-width="0.9"/>
      <path d="M -20 0 Q -14 -8 -8 0 Q -14 8 -20 0 Z" fill="${GREEN}"/>
      <path d="M 20 0 Q 14 -8 8 0 Q 14 8 20 0 Z" fill="${CYAN}"/>
      <circle cx="0" cy="0" r="3.2" fill="${GREEN_LIGHT}"/>
      <circle cx="0" cy="0" r="1.2" fill="${BLACK}"/>
    </g>`;
};

// Medallion seal used at bottom-center
const medallion = (cx, cy, { year = "MMXXVI", label = "LAUNCH PARTNER" } = {}) => `
  <g transform="translate(${cx} ${cy})">
    <circle r="52" fill="none" stroke="${GREEN}" stroke-width="2"/>
    <circle r="46" fill="none" stroke="${CYAN}" stroke-width="0.6"/>
    <circle r="34" fill="${BLACK}" stroke="${GREEN}" stroke-width="0.8"/>
    <text y="-6" text-anchor="middle" fill="${GREEN_LIGHT}"
          font-family="Georgia, 'Times New Roman', serif" font-style="italic"
          font-size="12" letter-spacing="1.2">EVIQ</text>
    <text y="10" text-anchor="middle" fill="${CYAN_LIGHT}"
          font-family="Georgia, 'Times New Roman', serif"
          font-size="14" font-weight="700" letter-spacing="2">${escape(year)}</text>
    <path id="cz-arc-top" d="M -44 0 A 44 44 0 0 1 44 0" fill="none"/>
    <text font-family="Georgia, 'Times New Roman', serif" font-size="8"
          fill="${GREEN}" letter-spacing="4">
      <textPath href="#cz-arc-top" startOffset="50%" text-anchor="middle">${escape(label)}</textPath>
    </text>
    <path id="cz-arc-bot" d="M -44 4 A 44 44 0 0 0 44 4" fill="none"/>
    <text font-family="Georgia, 'Times New Roman', serif" font-size="8"
          fill="${CYAN}" letter-spacing="4">
      <textPath href="#cz-arc-bot" startOffset="50%" text-anchor="middle">EARTH INITIATIVE</textPath>
    </text>
  </g>
`;

// Subtle guilloché pattern (thin diagonal hairlines) — adds engraved feel
const guilloche = () => {
    let lines = "";
    for (let i = -1100; i < 1100; i += 14) {
        lines += `<line x1="${i}" y1="0" x2="${i + 1100}" y2="1100" stroke="${GREEN}" stroke-width="0.25" opacity="0.05"/>`;
    }
    for (let i = -1100; i < 1100; i += 14) {
        lines += `<line x1="${i}" y1="1100" x2="${i + 1100}" y2="0" stroke="${CYAN}" stroke-width="0.25" opacity="0.04"/>`;
    }
    return `<g>${lines}</g>`;
};

export const buildCoaterzCertificateSVG = ({
    qrModules,
    recipient = "CoaterZ",
    subline = "The Gold Standard",
    year = "MMXXVI",
    dateLine = "Issued this Feb, MMXXVI",
    serial = "No. 001 · LAUNCH PARTNER",
    signatoryName = "EVIQ Earth Initiative",
    signatoryTitle = "Founding Council",
    titleTop = "CERTIFICATE OF",
    titleBottom = "Launch Partnership",
    body = [
        "In recognition of your distinguished commitment to the enduring health",
        "of the electric vehicle, and to the batteries, forests, oceans, and",
        "communities that our members are helping to protect — you are hereby",
    ],
    italicFooter = "named a founding Launch Partner of the EVIQ Earth Initiative.",
    medallionLabel = "LAUNCH PARTNER",
    logoUrl = EVIQ_LOGO_URL,
    badgeUrl = IMPACT_BADGE_URL,
} = {}) => {
    const W = 850;
    const H = 1100;
    const qrSize = 130;
    const qrX = 90;
    const qrY = H - 90 - qrSize;

    return `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}">
  <defs>
    <linearGradient id="cz-bg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${BLACK}"/>
      <stop offset="0.55" stop-color="${BLACK_SOFT}"/>
      <stop offset="1" stop-color="${BLACK}"/>
    </linearGradient>
    <linearGradient id="cz-accent" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="${GREEN}"/>
      <stop offset="1" stop-color="${CYAN}"/>
    </linearGradient>
    <linearGradient id="cz-frame" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" stop-color="${GREEN}"/>
      <stop offset="0.5" stop-color="${CYAN}"/>
      <stop offset="1" stop-color="${GREEN}"/>
    </linearGradient>
    <radialGradient id="cz-vignette" cx="0.5" cy="0.5" r="0.75">
      <stop offset="0.55" stop-color="#000000" stop-opacity="0"/>
      <stop offset="1" stop-color="#000000" stop-opacity="0.85"/>
    </radialGradient>
  </defs>

  <!-- Bleed / background -->
  <rect width="${W}" height="${H}" fill="url(#cz-bg)"/>
  ${guilloche()}
  <rect width="${W}" height="${H}" fill="url(#cz-vignette)"/>

  <!-- Double frame: green→cyan gradient outer, thin green inner -->
  <rect x="34" y="34" width="${W - 68}" height="${H - 68}"
        fill="none" stroke="url(#cz-frame)" stroke-width="2.4"/>
  <rect x="48" y="48" width="${W - 96}" height="${H - 96}"
        fill="none" stroke="${GREEN}" stroke-width="0.7" opacity="0.5"/>
  <rect x="58" y="58" width="${W - 116}" height="${H - 116}"
        fill="none" stroke="${CYAN}" stroke-width="0.4" opacity="0.4"/>

  <!-- Corner flourishes -->
  ${cornerFlourish(66, 66, 0)}
  ${cornerFlourish(W - 66, 66, 90)}
  ${cornerFlourish(W - 66, H - 66, 180)}
  ${cornerFlourish(66, H - 66, 270)}

  <!-- Top mark: EVIQ logo + wordmark -->
  <image href="${logoUrl}"
         x="${W / 2 - 44}" y="110" width="88" height="88"
         preserveAspectRatio="xMidYMid meet"/>

  <!-- EVIQ Impact Partner badge (official seal, top-right) -->
  <g transform="translate(${W - 132} 148) rotate(-6)">
    <circle r="60" fill="none" stroke="url(#cz-accent)" stroke-width="1.2" opacity="0.55"/>
    <circle r="52" fill="${BLACK}" opacity="0.35"/>
    <image href="${badgeUrl}"
           x="-46" y="-46" width="92" height="92"
           preserveAspectRatio="xMidYMid meet"/>
  </g>

  <text x="${W / 2}" y="228" text-anchor="middle" fill="${GREEN_LIGHT}"
        font-family="Georgia, 'Times New Roman', serif"
        font-size="13" letter-spacing="8">EVIQ · EARTH INITIATIVE</text>

  ${filigree(W / 2, 250, 280)}

  <!-- Title -->
  <text x="${W / 2}" y="304" text-anchor="middle" fill="${CREAM}"
        font-family="Georgia, 'Times New Roman', serif"
        font-size="17" letter-spacing="10">${escape(titleTop)}</text>
  <text x="${W / 2}" y="356" text-anchor="middle" fill="url(#cz-accent)"
        font-family="Georgia, 'Times New Roman', serif" font-style="italic"
        font-size="46" letter-spacing="1">${escape(titleBottom)}</text>

  ${filigree(W / 2, 388, 220)}

  <!-- Presented to -->
  <text x="${W / 2}" y="428" text-anchor="middle" fill="${CREAM}" opacity="0.6"
        font-family="Georgia, 'Times New Roman', serif"
        font-size="12" letter-spacing="7">PRESENTED TO</text>

  <!-- Recipient -->
  <text x="${W / 2}" y="510" text-anchor="middle" fill="${GREEN_LIGHT}"
        font-family="Georgia, 'Times New Roman', serif" font-style="italic"
        font-weight="400" font-size="82" letter-spacing="1">${escape(recipient)}</text>

  <text x="${W / 2}" y="546" text-anchor="middle" fill="${CYAN_LIGHT}" opacity="0.9"
        font-family="Georgia, 'Times New Roman', serif" font-style="italic"
        font-size="18" letter-spacing="4">${subline ? `— ${escape(subline)} —` : ""}</text>

  <!-- Body copy -->
  <g font-family="Georgia, 'Times New Roman', serif" fill="${CREAM}"
     font-size="16" opacity="0.85">
    <text x="${W / 2}" y="612" text-anchor="middle">
      ${escape(body[0] || "")}
    </text>
    <text x="${W / 2}" y="636" text-anchor="middle">
      ${escape(body[1] || "")}
    </text>
    <text x="${W / 2}" y="660" text-anchor="middle">
      ${escape(body[2] || "")}
    </text>
    <text x="${W / 2}" y="684" text-anchor="middle" font-style="italic" fill="${GREEN_LIGHT}">
      ${escape(italicFooter)}
    </text>
  </g>

  <!-- Medallion (bottom center) -->
  ${medallion(W / 2, 830, { year, label: medallionLabel })}

  <!-- QR code (bottom left) -->
  <g>
    <rect x="${qrX - 8}" y="${qrY - 8}" width="${qrSize + 16}" height="${qrSize + 16}"
          fill="none" stroke="${GREEN}" stroke-width="0.8"/>
    <rect x="${qrX - 4}" y="${qrY - 4}" width="${qrSize + 8}" height="${qrSize + 8}"
          fill="${BLACK}"/>
    ${buildQrGroup(qrModules, { x: qrX, y: qrY, size: qrSize })}
    <text x="${qrX + qrSize / 2}" y="${qrY + qrSize + 26}" text-anchor="middle"
          fill="${GREEN_LIGHT}" font-family="Georgia, 'Times New Roman', serif"
          font-size="10" letter-spacing="4">SCAN · EVIQ.PRO/APP</text>
  </g>

  <!-- Signature block (bottom right) -->
  <g transform="translate(${W - 90} ${qrY + qrSize / 2 - 20})" text-anchor="end">
    <line x1="-240" y1="0" x2="0" y2="0" stroke="url(#cz-accent)" stroke-width="0.8"/>
    <text y="20" fill="${GREEN_LIGHT}" font-family="Georgia, 'Times New Roman', serif"
          font-style="italic" font-size="18">${escape(signatoryName)}</text>
    <text y="40" fill="${CREAM}" opacity="0.6"
          font-family="Georgia, 'Times New Roman', serif"
          font-size="10" letter-spacing="4">${escape(signatoryTitle.toUpperCase())}</text>
    <text y="68" fill="${CREAM}" opacity="0.4"
          font-family="Georgia, 'Times New Roman', serif"
          font-size="9" letter-spacing="3">${escape(dateLine.toUpperCase())}</text>
  </g>

  <!-- Serial (bottom center under medallion) -->
  <text x="${W / 2}" y="928" text-anchor="middle"
        fill="${CYAN}" font-family="Georgia, 'Times New Roman', serif"
        font-size="10" letter-spacing="5">${escape(serial)}</text>
</svg>`;
};

export default buildCoaterzCertificateSVG;
